import { type DBSchema, type IDBPDatabase, openDB } from "idb"
import { type DeviceScope, vaultFingerprint } from "./device-unlock"
import { ALL_USERS, createRevocationMarker } from "./revocation-marker"

// ─── Unlock with Face ID / Touch ID (WebAuthn PRF) ───────────────────────────
//
// Opt-in, per browser, offered only where the WebAuthn PRF extension works.
// Same shape as device-unlock.ts, with the device key replaced by a key only
// the platform authenticator can re-create after user verification:
//
//   ENROL (Settings › Privacy, while unlocked — the DEK is in memory)
//   1. navigator.credentials.create(): a platform credential,
//      userVerification "required", residentKey "preferred", with the PRF
//      extension evaluated on a fresh random 32-byte `prfSalt`. We require
//      getClientExtensionResults().prf.enabled; if the create didn't also
//      return the PRF output, one get() evaluates it.
//   2. wrapping key = HKDF-SHA256(ikm = PRF output, salt = prfSalt,
//      info = HKDF_INFO) → AES-GCM-256, extractable:false, wrapKey/unwrapKey.
//   3. wrapKey("raw", DEK) under it with a fresh IV; the record's scope
//      fields are the AES-GCM additional data (tampering fails the unwrap).
//   4. IndexedDB stores { v, userId, householdId, vaultFp, credentialId,
//      prfSalt, iv, wrappedDek, createdAt }. Nothing else.
//
//   UNLOCK (the "Use Face ID / Touch ID" button on the unlock sheet)
//   credentials.get() for that credential id with the same PRF input →
//   the authenticator (after Face ID / Touch ID / device passcode) returns
//   the same PRF output → same wrapping key → unwrapKey → DEK.
//
// What is NEVER stored or sent anywhere: the PRF output, the wrapping key's
// bytes (it is non-extractable and only ever held in memory), the DEK's
// bytes, the PIN. The server learns nothing and no API changed: the
// credential's public key is discarded (there is no server-side assertion
// check — this is a local key-release, not a login).
//
// Scope + fail-closed rules match device-unlock.ts: the record is keyed by
// user id and bound to the household and a SHA-256 fingerprint of the
// server's PIN-wrapped DEK, so a PIN reset / recovery anywhere (new
// wrappedDek) invalidates it. A scope or fingerprint mismatch, a missing PRF
// result or a failed unwrap DELETES the record; the caller falls back to the
// PIN. A cancelled prompt keeps it (the person just chose the PIN).
//
// WHEN IT IS CLEARED — deliberately NOT the same set as remember-device:
//   • Lock now / inactivity auto-lock: KEPT. Remember-device is a silent
//     unlock, so a lock has to delete it or the lock protects nothing. Face ID
//     is never silent: every unlock needs a fresh user-verified ceremony on
//     the authenticator, so surviving a lock is exactly its point (lock the
//     tab, get back in with a glance) and costs nothing.
//   • Logout (sign-out.ts → forgetAllWebAuthn): cleared, so the next person to
//     sign in on this browser can't use it (it's scoped to the user anyway).
//   • PIN change / first PIN / enabling privacy (new DEK or new wrappedDek):
//     cleared — the fingerprint would no longer match anyway.
//   • Recovery-phrase use: cleared, same as remember-device.
//   • Privacy mode turned off: cleared.
//   • "Remove Face ID" in Settings › Privacy: cleared.
// The platform credential itself stays in the OS keychain (a web page can't
// delete passkeys); without this record it can release nothing.

const WEBAUTHN_RECORD_VERSION = 1
const DB_NAME = "paisa-webauthn-unlock"
const STORE = "credentials"
const REVOKE_PREFIX = "paisa-webauthn-unlock-revoked"
const HKDF_INFO = "paisa/webauthn-prf/dek-wrap/v1"
const TIMEOUT_MS = 60_000

export interface WebAuthnRecord {
  v: number
  userId: string
  householdId: string
  vaultFp: string
  credentialId: ArrayBuffer
  prfSalt: Uint8Array<ArrayBuffer>
  iv: Uint8Array<ArrayBuffer>
  wrappedDek: ArrayBuffer
  createdAt: number
}

interface WebAuthnDB extends DBSchema {
  credentials: { key: string; value: WebAuthnRecord }
}

// ── Injectable environment (WebAuthn isn't available in happy-dom) ──────────

type Credentials = Pick<CredentialsContainer, "create" | "get">
interface PkcStatics {
  getClientCapabilities?: () => Promise<Record<string, boolean | undefined>>
  isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>
}
export interface WebAuthnEnv {
  credentials?: Credentials
  PublicKeyCredential?: PkcStatics
}

let envOverride: WebAuthnEnv | null = null
/** Test hook: swap in a fake authenticator (null restores the browser's). */
export function __setWebAuthnEnv(env: WebAuthnEnv | null) {
  envOverride = env
}

function getEnv(): { credentials?: Credentials; pkc?: PkcStatics } {
  if (envOverride)
    return { credentials: envOverride.credentials, pkc: envOverride.PublicKeyCredential }
  const g = globalThis as { PublicKeyCredential?: PkcStatics; navigator?: Navigator }
  return { credentials: g.navigator?.credentials, pkc: g.PublicKeyCredential }
}

// ── Revocation markers (fail closed after a failed delete, across reloads) ──
// Same pattern as device-unlock.ts: a localStorage marker (user id or
// ALL_USERS, no key material) is written before every delete and cleared
// only once the delete is confirmed. While set, unlock refuses the record and
// retries the delete on each load.

const revoked = createRevocationMarker(REVOKE_PREFIX)

/** Retry a pending delete; true when the caller must refuse this record. */
async function refuseIfRevoked(userId: string): Promise<boolean> {
  if (!revoked.isRevoked(userId)) return false
  if (revoked.allRevoked()) await forgetAllWebAuthn()
  else await forgetWebAuthn(userId)
  return true
}

// ── IndexedDB ────────────────────────────────────────────────────────────────

function withTimeout<T>(p: Promise<T>, label: string, ms = 3000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out`)), ms)
    p.then(
      (v) => {
        clearTimeout(t)
        resolve(v)
      },
      (e) => {
        clearTimeout(t)
        reject(e)
      },
    )
  })
}

async function withDb<T>(label: string, fn: (db: IDBPDatabase<WebAuthnDB>) => Promise<T>) {
  return withTimeout(
    (async () => {
      const db = await openDB<WebAuthnDB>(DB_NAME, 1, {
        upgrade(d) {
          d.createObjectStore(STORE, { keyPath: "userId" })
        },
      })
      try {
        return await fn(db)
      } finally {
        db.close()
      }
    })(),
    label,
  )
}

// ── Crypto helpers ───────────────────────────────────────────────────────────

const enc = (s: string) => new TextEncoder().encode(s)

function random(n: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(n))
}

function b64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
}

function toArrayBuffer(src: BufferSource): ArrayBuffer {
  if (src instanceof ArrayBuffer) return src.slice(0)
  const view = src as ArrayBufferView
  return new Uint8Array(view.buffer, view.byteOffset, view.byteLength).slice().buffer
}

function sameBytes(a: ArrayBuffer, b: ArrayBuffer): boolean {
  const x = new Uint8Array(a)
  const y = new Uint8Array(b)
  return x.length === y.length && x.every((v, i) => v === y[i])
}

function additionalData(r: Omit<WebAuthnRecord, "prfSalt" | "iv" | "wrappedDek">) {
  return enc(
    JSON.stringify([r.v, r.userId, r.householdId, r.vaultFp, b64(r.credentialId), r.createdAt]),
  )
}

/**
 * PRF output → the AES-GCM key that wraps the DEK. HKDF, not the raw PRF
 * bytes, so the key is domain-separated (HKDF_INFO) and salted per record.
 * extractable:false — the wrapping key's bytes never reach JS.
 */
export async function deriveWrappingKey(
  prfOutput: BufferSource,
  prfSalt: Uint8Array<ArrayBuffer>,
): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey("raw", prfOutput, "HKDF", false, ["deriveKey"])
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: prfSalt, info: enc(HKDF_INFO) },
    ikm,
    { name: "AES-GCM", length: 256 },
    false,
    ["wrapKey", "unwrapKey"],
  )
}

/** A stable, non-identifying WebAuthn user handle for this Paisa user. */
async function userHandle(userId: string): Promise<Uint8Array<ArrayBuffer>> {
  const d = await crypto.subtle.digest("SHA-256", enc(`paisa-unlock:${userId}`))
  return new Uint8Array(d).slice(0, 16)
}

type PrfResults = { prf?: { enabled?: boolean; results?: { first?: BufferSource } } }

function prfFrom(cred: PublicKeyCredential): PrfResults["prf"] {
  try {
    return (cred.getClientExtensionResults() as PrfResults).prf
  } catch {
    return undefined
  }
}

function isCancel(e: unknown): boolean {
  const name = typeof e === "object" && e !== null ? (e as { name?: unknown }).name : undefined
  return name === "NotAllowedError" || name === "AbortError"
}

async function evaluatePrf(
  credentials: Credentials,
  credentialId: ArrayBuffer,
  prfSalt: Uint8Array<ArrayBuffer>,
): Promise<{ output: BufferSource } | { cancelled: true } | { error: true } | null> {
  let assertion: Credential | null
  try {
    assertion = await credentials.get({
      publicKey: {
        challenge: random(32),
        allowCredentials: [{ type: "public-key", id: credentialId }],
        userVerification: "required",
        timeout: TIMEOUT_MS,
        extensions: { prf: { eval: { first: prfSalt } } } as AuthenticationExtensionsClientInputs,
      },
    })
  } catch (e) {
    // Dismissed prompt → cancelled; anything else (authenticator busy, etc.)
    // is treated as transient: fall back to the PIN but keep the record.
    return isCancel(e) ? { cancelled: true } : { error: true }
  }
  if (!assertion) return { cancelled: true }
  const cred = assertion as PublicKeyCredential
  if (!sameBytes(cred.rawId, credentialId)) return null
  const first = prfFrom(cred)?.results?.first
  return first ? { output: first } : null
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Whether to OFFER Face ID / Touch ID at all: WebAuthn present and the PRF
 * extension supported. With getClientCapabilities() (newer browsers) we
 * require `extension:prf`; without it we require a user-verifying platform
 * authenticator and verify prf.enabled when enrolling. Never throws.
 */
export async function isWebAuthnUnlockSupported(): Promise<boolean> {
  const { credentials, pkc } = getEnv()
  if (!pkc || !credentials) return false
  try {
    if (typeof pkc.getClientCapabilities === "function") {
      const caps = await pkc.getClientCapabilities()
      return caps["extension:prf"] === true && caps.userVerifyingPlatformAuthenticator !== false
    }
    if (typeof pkc.isUserVerifyingPlatformAuthenticatorAvailable === "function") {
      return await pkc.isUserVerifyingPlatformAuthenticatorAvailable()
    }
  } catch {
    return false
  }
  return false
}

export type EnrolResult =
  | { ok: true }
  | { ok: false; reason: "unsupported" | "cancelled" | "prf-unavailable" | "error" }

/**
 * Enrol this browser: create a platform credential with PRF and store the
 * DEK wrapped under the PRF-derived key. Any failure stores nothing.
 */
export async function enrolWebAuthn(
  dek: CryptoKey,
  scope: DeviceScope & { userName: string },
): Promise<EnrolResult> {
  const { credentials } = getEnv()
  if (!credentials || !scope.userId || !scope.householdId || !scope.wrappedDek) {
    return { ok: false, reason: "unsupported" }
  }
  // A pending revocation must be confirmed before a new record is written.
  if (revoked.isRevoked(scope.userId)) {
    await refuseIfRevoked(scope.userId)
    if (revoked.isRevoked(scope.userId)) return { ok: false, reason: "error" }
  }
  try {
    const prfSalt = random(32)
    let created: Credential | null
    try {
      created = await credentials.create({
        publicKey: {
          challenge: random(32),
          rp: { name: "Paisa" },
          user: {
            id: await userHandle(scope.userId),
            name: scope.userName || "Paisa",
            displayName: scope.userName || "Paisa",
          },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 },
            { type: "public-key", alg: -257 },
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            residentKey: "preferred",
            requireResidentKey: false,
            userVerification: "required",
          },
          attestation: "none",
          timeout: TIMEOUT_MS,
          extensions: { prf: { eval: { first: prfSalt } } } as AuthenticationExtensionsClientInputs,
        },
      })
    } catch (e) {
      return { ok: false, reason: isCancel(e) ? "cancelled" : "error" }
    }
    if (!created) return { ok: false, reason: "cancelled" }
    const cred = created as PublicKeyCredential
    const prf = prfFrom(cred)
    if (!prf?.enabled) return { ok: false, reason: "prf-unavailable" }
    const credentialId = toArrayBuffer(cred.rawId)

    // Some authenticators return the PRF output at create; others only on get.
    let output = prf.results?.first
    if (!output) {
      const evaluated = await evaluatePrf(credentials, credentialId, prfSalt)
      if (!evaluated) return { ok: false, reason: "prf-unavailable" }
      if ("cancelled" in evaluated) return { ok: false, reason: "cancelled" }
      if ("error" in evaluated) return { ok: false, reason: "error" }
      output = evaluated.output
    }

    const wrappingKey = await deriveWrappingKey(output, prfSalt)
    const meta = {
      v: WEBAUTHN_RECORD_VERSION,
      userId: scope.userId,
      householdId: scope.householdId,
      vaultFp: await vaultFingerprint(scope.wrappedDek),
      credentialId,
      createdAt: Date.now(),
    }
    const iv = random(12)
    const wrappedDek = await crypto.subtle.wrapKey("raw", dek, wrappingKey, {
      name: "AES-GCM",
      iv,
      additionalData: additionalData(meta),
    })
    await withDb("write", (db) => db.put(STORE, { ...meta, prfSalt, iv, wrappedDek }))
    return { ok: true }
  } catch {
    await forgetWebAuthn(scope.userId)
    return { ok: false, reason: "error" }
  }
}

async function loadRecord(userId: string): Promise<WebAuthnRecord | null> {
  try {
    return (await withDb("read", (db) => db.get(STORE, userId))) ?? null
  } catch {
    return null
  }
}

async function recordMatches(rec: WebAuthnRecord, scope: DeviceScope): Promise<boolean> {
  return (
    rec.v === WEBAUTHN_RECORD_VERSION &&
    rec.userId === scope.userId &&
    rec.householdId === scope.householdId &&
    rec.vaultFp === (await vaultFingerprint(scope.wrappedDek)) &&
    typeof rec.createdAt === "number" &&
    rec.credentialId instanceof ArrayBuffer &&
    rec.prfSalt instanceof Uint8Array &&
    rec.iv instanceof Uint8Array &&
    rec.wrappedDek instanceof ArrayBuffer
  )
}

/**
 * Whether this browser holds a VALID enrolment for `scope`. A record that no
 * longer matches (other household, PIN reset elsewhere, bad shape) is deleted.
 */
export async function hasWebAuthnEnrolment(scope: DeviceScope): Promise<boolean> {
  if (!scope.userId || !scope.householdId || !scope.wrappedDek) return false
  if (await refuseIfRevoked(scope.userId)) return false
  const rec = await loadRecord(scope.userId)
  if (!rec) return false
  if (await recordMatches(rec, scope)) return true
  await forgetWebAuthn(scope.userId)
  return false
}

export type WebAuthnUnlockResult =
  | { dek: CryptoKey }
  | { dek: null; reason: "none" | "cancelled" | "mismatch" | "failed" }

/**
 * Unlock with Face ID / Touch ID. Returns the DEK, or a reason to fall back
 * to the PIN: "none" (not enrolled), "cancelled" (prompt dismissed — record
 * kept), "mismatch" (scope/fingerprint changed — record deleted), "failed"
 * (no PRF result, wrong credential or unwrap failure — record deleted,
 * except for a transient authenticator error).
 */
export async function unlockWithWebAuthn(scope: DeviceScope): Promise<WebAuthnUnlockResult> {
  const { credentials } = getEnv()
  if (!credentials || !scope.userId) return { dek: null, reason: "none" }
  if (await refuseIfRevoked(scope.userId)) return { dek: null, reason: "none" }
  const rec = await loadRecord(scope.userId)
  if (!rec) return { dek: null, reason: "none" }
  if (!(await recordMatches(rec, scope))) {
    await forgetWebAuthn(scope.userId)
    return { dek: null, reason: "mismatch" }
  }
  const evaluated = await evaluatePrf(credentials, rec.credentialId, rec.prfSalt)
  if (evaluated && "cancelled" in evaluated) return { dek: null, reason: "cancelled" }
  if (evaluated && "error" in evaluated) return { dek: null, reason: "failed" }
  if (!evaluated) {
    await forgetWebAuthn(scope.userId)
    return { dek: null, reason: "failed" }
  }
  try {
    const wrappingKey = await deriveWrappingKey(evaluated.output, rec.prfSalt)
    // Same shape unwrapDEK / unlockWithDevice produce (extractable, needed to
    // re-wrap for the recovery phrase or remember-device).
    const dek = await crypto.subtle.unwrapKey(
      "raw",
      rec.wrappedDek,
      wrappingKey,
      { name: "AES-GCM", iv: rec.iv, additionalData: additionalData(rec) },
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"],
    )
    return { dek }
  } catch {
    await forgetWebAuthn(scope.userId)
    return { dek: null, reason: "failed" }
  }
}

/** Remove this browser's Face ID enrolment for one user. Never throws; false if the delete failed. */
export async function forgetWebAuthn(userId: string): Promise<boolean> {
  if (!userId) return true
  revoked.mark(userId)
  try {
    await withDb("delete", (db) => db.delete(STORE, userId))
    revoked.clear(userId)
    return true
  } catch {
    return false
  }
}

/** Remove every Face ID enrolment on this browser (logout). Never throws. */
export async function forgetAllWebAuthn(): Promise<boolean> {
  revoked.mark(ALL_USERS)
  try {
    await withDb("clear", (db) => db.clear(STORE))
    revoked.clearAll()
    return true
  } catch {
    return false
  }
}
